import { debounce } from 'lodash';
import { Link } from 'react-router-dom';
import { useMemo, useState, useCallback } from 'react';

import {
  Card,
  Table,
  Stack,
  TableRow,
  useTheme,
  TableBody,
  TableCell,
  TextField,
  TableContainer,
  InputAdornment,
} from '@mui/material';

import { paths } from 'src/routes/paths';

import { useTranslate } from 'src/locales';
import { PageOptions } from 'src/services/api.types';
import { AdminOrganization } from 'src/services/admin/admin.types';
import { useGetAllOrganizationsQuery } from 'src/services/admin/admin.service';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import {
  useTable,
  emptyRows,
  TableNoData,
  TableEmptyRows,
  TableHeadCustom,
  TablePaginationCustom,
} from 'src/components/table';

const default_page: PageOptions = {
  page: 1,
  per_page: 20,
};

export default function GlobalOrganizationsTable() {
  const table = useTable();

  const { t } = useTranslate();

  const [pageOptions, setPageOptions] = useState<PageOptions>(default_page);

  const [searchQuery, setSearchQuery] = useState('');

  const { currentData } = useGetAllOrganizationsQuery({
    ...pageOptions,
    search_query: searchQuery,
  });

  const denseHeight = table.dense ? 56 : 56 + 20;

  const tableLabels = useMemo(
    () => [
      { id: 'title', label: 'ID' },
      { id: 'company_name', label: 'Company Name' },
      { id: 'candidate_name', label: 'Candidate Name' },
      { id: 'status', label: 'Status' },
      { id: 'type', label: 'Type' },
    ],
    []
  );

  const notFound = !currentData?.count;

  const onChangePage = (_event: any, pageNumber: number) => {
    setPageOptions((prev) => ({ ...prev, page: pageNumber }));
  };

  const onChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPageOptions((prev) => ({ ...prev, per_page: parseInt(event.target.value, 10) }));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearch = useCallback(
    debounce((value: string) => {
      setSearchQuery(value);
    }, 500),
    [setSearchQuery]
  );

  return (
    <Card>
      <Stack
        sx={{
          p: 2.5,
          pr: { xs: 2.5, md: 1 },
        }}
      >
        <TextField
          onChange={(e) => debounceSearch(e.target.value)}
          label="Search"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <TableContainer sx={{ overflow: 'unset' }}>
        <Scrollbar>
          <Table sx={{ minWidth: 680 }} size={table.dense ? 'small' : 'medium'}>
            <TableHeadCustom headLabel={tableLabels} />

            <TableBody>
              {currentData?.results?.map((row) => <OrganizationsTableRow key={row.id} row={row} />)}

              <TableEmptyRows
                height={denseHeight}
                emptyRows={emptyRows(table.page, table.rowsPerPage, currentData?.count ?? 0)}
              />

              <TableNoData notFound={notFound} />
            </TableBody>
          </Table>
        </Scrollbar>
      </TableContainer>

      <TablePaginationCustom
        count={currentData?.count ?? 0}
        page={pageOptions.page}
        rowsPerPage={pageOptions.per_page}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        dense={table.dense}
        onChangeDense={table.onChangeDense}
      />
    </Card>
  );
}

const OrganizationsTableRow = ({ row }: { row: AdminOrganization }) => {
  const theme = useTheme();

  return (
    <TableRow>
      <TableCell>
        <Link
          style={{ color: theme.palette.text.primary }}
          to={`${paths.dashboard.restricted.organizations.root}/${row.id}`}
        >
          {row.id}
        </Link>
      </TableCell>
      <TableCell>{row.company_profile?.company_name}</TableCell>
      <TableCell>{`${row.candidateProfile?.first_name ?? ''}${
        row.candidateProfile?.last_name ?? '-'
      }`}</TableCell>
      <TableCell>{row.status}</TableCell>
      <TableCell>{row.type}</TableCell>
    </TableRow>
  );
};
